import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag, Menu, X, Quote, Sparkles, Heart, Download, Check, BookOpen } from "lucide-react";
import { FreeToolsDropdown, FreeToolsMobileLinks } from "@/components/FreeToolsMenu";
import {
  AMAZON_BOOK_URL,
  isBookLive,
} from "../config/bookStatus";

// ============================================================================
//  Mind Body Spirit HYGIENE — HOMEPAGE (Book-First Single Hero)
//  ----------------------------------------------------------------------------
//  Strategy: cold visitors land on ONE product (the book) + ONE story
//  (Temple's "to the studs" rebuild). All other products are reached via
//  the small "More from Temple →" link at the bottom, which goes to /shop.
//  Palette: OCEAN (Deep Teal #1B6B6E / Mid #3D9096 / Light #7BC8CB /
//           Cream #F4F1EC / Soft Mint #EBF6F6 / Dark #1C2B2B). No gold.
//  Voice: caveman-simple. Sign "— Temple". Tagline "It's not a practice.
//         It's hygiene." appears identically.
// ============================================================================

const OCEAN = {
  deep: "#1B6B6E",
  mid: "#3D9096",
  light: "#7BC8CB",
  cream: "#F4F1EC",
  mint: "#EBF6F6",
  dark: "#1C2B2B",
};

// ----------------------------------------------------------------------------
//  Header — minimal: logo + "Buy the Book" + mobile hamburger
// ----------------------------------------------------------------------------
const BOOK_CHOOSER = "/book#editions";

const PaperbackLine = ({ testid, onDark = false }) => (
  <p className="font-sans text-sm mt-4" style={{ color: onDark ? OCEAN.cream : OCEAN.dark, opacity: 0.8 }}>
    Prefer it in your hands?{" "}
    <a
      href={AMAZON_BOOK_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="underline underline-offset-2 font-semibold hover:opacity-80 transition-opacity"
      style={{ color: onDark ? OCEAN.cream : OCEAN.deep }}
    >
      Paperback · $19.99 on Amazon
    </a>
  </p>
);

const HomeHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);

  const navLinks = [
    { to: "/book", label: "The Book" },
    { to: "/build-your-own", label: "Build Your Own" },
    // Shop temporarily hidden until P1 legacy Stripe link migration (dead buy-links).
    { to: "/about", label: "About Temple" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="home-header"
      className="fixed top-0 w-full z-50 backdrop-blur-xl border-b"
      style={{
        backgroundColor: "rgba(244, 241, 236, 0.92)",
        borderColor: "rgba(27, 107, 110, 0.15)",
      }}
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
        <Link
          to="/"
          onClick={close}
          data-testid="home-logo-link"
          className="flex items-center gap-3"
        >
          <img
            src="/downloads/MBSH-logo-TM-e4476e.png"
            alt="Mind Body Spirit HYGIENE™"
            className="h-10 md:h-12 w-auto"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`home-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-sans text-sm tracking-wide transition-opacity hover:opacity-70"
              style={{ color: OCEAN.deep }}
            >
              {l.label}
            </Link>
          ))}
          <FreeToolsDropdown color={OCEAN.deep} testidPrefix="home-" />
          <Link
            to={BOOK_CHOOSER}
            data-testid="home-header-buy-book"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2 font-sans font-semibold text-sm transition-all hover:-translate-y-0.5 shadow-md"
            style={{ backgroundColor: OCEAN.deep, color: OCEAN.cream }}
          >
            <ShoppingBag className="w-4 h-4" />
            Buy the Book
          </Link>
        </nav>

        {/* Mobile right side */}
        <div className="flex md:hidden items-center gap-2">
          <Link
            to={BOOK_CHOOSER}
            data-testid="home-header-buy-book-mobile"
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-sans font-semibold text-xs shadow-md"
            style={{ backgroundColor: OCEAN.deep, color: OCEAN.cream }}
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            Buy
          </Link>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            data-testid="home-mobile-menu-toggle"
            className="p-2 rounded-md transition-colors"
            style={{ color: OCEAN.deep }}
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          data-testid="home-mobile-menu-drawer"
          className="md:hidden border-t shadow-lg"
          style={{
            backgroundColor: OCEAN.cream,
            borderColor: "rgba(27, 107, 110, 0.15)",
          }}
        >
          <nav className="px-5 py-3 flex flex-col">
            <FreeToolsMobileLinks onNavigate={close} testidPrefix="home-" />
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={close}
                data-testid={`home-mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-sans text-base font-medium py-3 px-2 border-b"
                style={{
                  color: OCEAN.dark,
                  borderColor: "rgba(27, 107, 110, 0.1)",
                }}
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

// ----------------------------------------------------------------------------
// ----------------------------------------------------------------------------
//  WorkbookHero — email-first, workbook-first single-fold hero
//  (Replaces the older BookHero. Data drove this: /workbook = 0% bounce,
//   67% homepage bounce, most visitors head straight to /about.)
// ----------------------------------------------------------------------------
const WORKBOOK_PDF_URL = "/workbook/I_GET_TO_Workbook.pdf";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SUBSCRIBED_KEY = "mbsh_subscribed";
const SUBSCRIBED_NAME_KEY = "mbsh_first_name";

const WorkbookHero = () => {
  const live = isBookLive();
  const [isReturning, setIsReturning] = useState(false);
  const [returningName, setReturningName] = useState("");
  const [formData, setFormData] = useState({ firstName: "", email: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  // Returning-visitor detection (localStorage flag set by ALL opt-in surfaces)
  useEffect(() => {
    try {
      if (localStorage.getItem(SUBSCRIBED_KEY) === "true") {
        setIsReturning(true);
        const nm = localStorage.getItem(SUBSCRIBED_NAME_KEY);
        if (nm) setReturningName(nm);
      }
    } catch (_) { /* ignore */ }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    // Synchronous re-entry guard — prevents double-fire from double-tap/fast double-click.
    // The button's `disabled` attribute alone is not enough (React re-render is async).
    if (status.state === "submitting" || status.state === "success") return;
    if (!formData.email || !formData.email.includes("@")) {
      setStatus({ state: "error", message: "Please enter a valid email." });
      return;
    }
    setStatus({ state: "submitting", message: "" });
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: formData.firstName || "Friend",
          email: formData.email.trim().toLowerCase(),
          group_id: "192377413184783762", // MailerLite group: "Workbook - Homepage Hero"
          source: "homepage-workbook-hero",
        }),
      });
      if (!res.ok) throw new Error("subscribe failed");

      // Persist returning-visitor state across ALL future visits
      try {
        localStorage.setItem(SUBSCRIBED_KEY, "true");
        if (formData.firstName) localStorage.setItem(SUBSCRIBED_NAME_KEY, formData.firstName);
      } catch (_) { /* ignore */ }

      // GA4 Lead event
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          method: "homepage_workbook",
          currency: "USD",
          value: 0,
        });
      }
      // Meta Pixel Lead event (safe no-op until pixel base code is installed)
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", {
          content_name: "I GET TO Workbook",
          content_category: "lead-magnet",
          value: 0,
          currency: "USD",
        });
      }

      setStatus({ state: "success", message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        message: "Something went sideways. Please try again — or email temple@mindbodyspirithygienetools.com",
      });
    }
  };

  const clearAndStartFresh = () => {
    try {
      localStorage.removeItem(SUBSCRIBED_KEY);
      localStorage.removeItem(SUBSCRIBED_NAME_KEY);
    } catch (_) { /* ignore */ }
    setIsReturning(false);
    setReturningName("");
  };

  const heroBg = {
    background: `linear-gradient(165deg, ${OCEAN.deep} 0%, ${OCEAN.mid} 100%)`,
  };
  const radial = {
    background: `radial-gradient(circle at 85% 25%, rgba(123, 200, 203, 0.22) 0%, transparent 55%)`,
  };

  // ═══════════════ RETURNING-VISITOR VIEW ═══════════════
  if (isReturning) {
    return (
      <section
        data-testid="home-hero-returning"
        className="relative pt-24 md:pt-28 pb-16 md:pb-20 overflow-hidden"
        style={heroBg}
      >
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={radial} />
        <div className="relative max-w-3xl mx-auto px-5 md:px-8 text-center" style={{ color: OCEAN.cream }}>
          <p className="font-sans text-[11px] tracking-[0.32em] uppercase font-semibold mb-4" style={{ color: OCEAN.light }}>
            Welcome back{returningName ? `, ${returningName}` : ", dear one"}
          </p>
          <h1
            className="font-serif tracking-tighter leading-[1.05] mb-5"
            style={{ fontSize: "clamp(2rem, 5.5vw, 3.75rem)" }}
            data-testid="home-hero-returning-headline"
          >
            Ready for the next layer?
          </h1>
          <p className="font-serif italic text-lg md:text-xl mb-8" style={{ color: OCEAN.light }}>
            You&rsquo;ve got the workbook. Here&rsquo;s what&rsquo;s next.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            {live && (
              <Link
                to={BOOK_CHOOSER}
                data-testid="home-hero-returning-book-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
                style={{ backgroundColor: OCEAN.cream, color: OCEAN.deep }}
              >
                <BookOpen className="w-5 h-5" />
                Read the book &middot; $9.99 instant PDF
              </Link>
            )}
            <Link
              to="/blog/the-not-so-perfect-storm"
              data-testid="home-hero-returning-blog-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 font-sans font-medium tracking-wide transition-all border-2"
              style={{ color: OCEAN.cream, borderColor: "rgba(244,241,236,0.45)" }}
            >
              Latest essay: The &ldquo;Not So&rdquo; Perfect Storm
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <button
            type="button"
            onClick={clearAndStartFresh}
            data-testid="home-hero-clear-flag"
            className="font-sans text-xs underline-offset-4 hover:underline transition-opacity"
            style={{ color: "rgba(244,241,236,0.6)" }}
          >
            Not you? Start fresh &rarr;
          </button>
        </div>
      </section>
    );
  }

  // ═══════════════ SUCCESS STATE (immediately after opt-in) ═══════════════
  if (status.state === "success") {
    return (
      <section
        data-testid="home-hero-success"
        className="relative pt-24 md:pt-28 pb-16 md:pb-20 overflow-hidden"
        style={heroBg}
      >
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={radial} />
        <div className="relative max-w-2xl mx-auto px-5 md:px-8 text-center" style={{ color: OCEAN.cream }}>
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-6"
            style={{ backgroundColor: "rgba(244,241,236,0.15)" }}
          >
            <Check className="w-7 h-7" style={{ color: OCEAN.light }} />
          </div>
          <h1
            className="font-serif tracking-tighter leading-[1.05] mb-4"
            style={{ fontSize: "clamp(1.75rem, 5vw, 3rem)" }}
            data-testid="home-hero-success-headline"
          >
            Welcome home, dear one.
          </h1>
          <p className="font-sans text-base md:text-lg leading-relaxed mb-8" style={{ color: "rgba(244,241,236,0.88)" }}>
            Check your inbox in the next minute. And here&rsquo;s your instant copy:
          </p>
          <a
            href={WORKBOOK_PDF_URL}
            download="I_GET_TO_Workbook.pdf"
            data-testid="home-hero-download-btn"
            className="inline-flex items-center justify-center gap-3 rounded-full px-8 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
            style={{ backgroundColor: OCEAN.cream, color: OCEAN.deep }}
            onClick={() => {
              if (typeof window !== "undefined" && typeof window.gtag === "function") {
                window.gtag("event", "download_workbook_pdf", { source: "homepage-hero" });
              }
            }}
          >
            <Download className="w-5 h-5" />
            Download the workbook (PDF)
          </a>
          <p className="mt-6 font-sans text-xs" style={{ color: "rgba(244,241,236,0.65)" }}>
            Scroll down &mdash; there&rsquo;s more waiting for you.
          </p>
        </div>
      </section>
    );
  }

  // ═══════════════ FIRST-TIME VISITOR VIEW (default) ═══════════════
  return (
    <section
      data-testid="home-workbook-hero"
      className="relative pt-24 md:pt-28 pb-16 md:pb-20 overflow-hidden"
      style={heroBg}
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={radial} />
      <div className="relative max-w-3xl mx-auto px-5 md:px-8 text-center" style={{ color: OCEAN.cream }}>
        <p
          data-testid="home-hero-eyebrow"
          className="font-sans text-[11px] tracking-[0.32em] uppercase font-semibold mb-4"
          style={{ color: OCEAN.light }}
        >
          Free &middot; Instant download
        </p>

        <h1
          data-testid="home-hero-headline"
          className="font-serif tracking-tighter leading-[1.02] mb-5"
          style={{ fontSize: "clamp(2rem, 6vw, 4.25rem)" }}
        >
          Stop saying{" "}
          <span className="italic" style={{ color: "rgba(244,241,236,0.7)" }}>
            &ldquo;I have to.&rdquo;
          </span>
          <br className="hidden sm:block" /> Start living{" "}
          <span className="italic" style={{ color: OCEAN.light }}>
            &ldquo;I GET TO.&rdquo;
          </span>
        </h1>

        <p
          data-testid="home-hero-benefit"
          className="font-sans text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto"
          style={{ color: "rgba(244,241,236,0.88)" }}
        >
          The I GET TO Workbook &mdash; 27 pages of real-tool reframes for the messy middle. Yours in 30 seconds.
        </p>

        <form
          onSubmit={onSubmit}
          data-testid="home-hero-form"
          className="max-w-md mx-auto flex flex-col gap-3"
        >
          <input
            type="text"
            placeholder="First name"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            data-testid="home-hero-firstname-input"
            className="w-full rounded-full px-6 py-3.5 font-sans text-base border-0 outline-none focus:ring-2 focus:ring-offset-2"
            style={{ backgroundColor: "rgba(244,241,236,0.94)", color: OCEAN.deep, focusRingColor: OCEAN.light }}
            autoComplete="given-name"
          />
          <input
            type="email"
            required
            placeholder="Your email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            data-testid="home-hero-email-input"
            className="w-full rounded-full px-6 py-3.5 font-sans text-base border-0 outline-none focus:ring-2 focus:ring-offset-2"
            style={{ backgroundColor: "rgba(244,241,236,0.94)", color: OCEAN.deep }}
            autoComplete="email"
          />
          <button
            type="submit"
            disabled={status.state === "submitting"}
            data-testid="home-hero-submit-btn"
            className="w-full rounded-full px-7 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            style={{ backgroundColor: OCEAN.cream, color: OCEAN.deep }}
          >
            {status.state === "submitting" ? (
              <>Sending&hellip;</>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Send me the workbook
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
          {status.state === "error" && (
            <p className="font-sans text-sm text-center" style={{ color: "#FFD1D1" }} data-testid="home-hero-error">
              {status.message}
            </p>
          )}
        </form>

        <p
          data-testid="home-hero-trust-line"
          className="font-sans text-xs tracking-wide mt-4"
          style={{ color: "rgba(244,241,236,0.65)" }}
        >
          Free forever. No spam. Unsubscribe anytime.
        </p>

        <div className="mt-8 pt-6 border-t border-white/10">
          <Link
            to="/blog/the-not-so-perfect-storm"
            data-testid="home-hero-already-have-it"
            className="font-sans text-sm underline-offset-4 hover:underline transition-opacity"
            style={{ color: "rgba(244,241,236,0.7)" }}
          >
            Already have it? Read the latest essay &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
};

// Preserve the old symbol so nothing that imports it downstream breaks.
const BookHero = WorkbookHero;

// ----------------------------------------------------------------------------
//  Trust Strip — 3 micro-credibility items right under the hero
// ----------------------------------------------------------------------------
const TrustStrip = () => {
  const items = [
    {
      label: "Built from the studs up",
      sub: "By a woman who lived the rebuild",
    },
    {
      label: "The A.C.T. NOW Method™",
      sub: "A daily framework, not a quote book",
    },
    {
      label: "Daily, not someday",
      sub: "It\u2019s not a practice. It\u2019s hygiene.",
    },
  ];
  return (
    <section
      data-testid="home-trust-strip"
      className="py-8 md:py-10"
      style={{ backgroundColor: OCEAN.mint }}
    >
      <div className="max-w-5xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
        {items.map((it, i) => (
          <div
            key={it.label}
            data-testid={`home-trust-${i + 1}`}
            className="text-center md:text-left"
          >
            <p
              className="font-serif text-lg md:text-xl mb-1"
              style={{ color: OCEAN.deep }}
            >
              {it.label}
            </p>
            <p
              className="font-sans text-sm"
              style={{ color: OCEAN.dark, opacity: 0.7 }}
            >
              {it.sub}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
//  What's Inside — 3 caveman-simple bullets (the promise)
// ----------------------------------------------------------------------------
const WhatsInside = () => {
  const bullets = [
    {
      title: "The A.C.T. NOW Method™",
      body: "Awareness · Clarity · Tapped In · No Opportunity Wasted. A four-step pattern you can run anywhere — in the parking lot, at the kitchen sink, in the hard conversation.",
    },
    {
      title: "Honest language for the messy middle",
      body: "Divorce. Grief. Estrangement. Health scares. The middle of the night. The parts of the story nobody warned you about — named, validated, and made workable.",
    },
    {
      title: "Daily Hygiene tools, not a practice",
      body: "Brush your teeth. Wash your face. Keep your inner world. The whole point: caveman-simple tools you actually use, not a course you abandon by Wednesday.",
    },
  ];

  return (
    <section
      data-testid="home-whats-inside"
      className="py-16 md:py-24"
      style={{ backgroundColor: OCEAN.cream }}
    >
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <div className="mb-10 md:mb-14 max-w-2xl">
          <p
            className="font-sans text-[11px] tracking-[0.32em] uppercase font-semibold mb-3"
            style={{ color: OCEAN.mid }}
          >
            Inside the book
          </p>
          <h2
            className="font-serif tracking-tighter leading-[1.05]"
            style={{
              fontSize: "clamp(1.85rem, 4.5vw, 3rem)",
              color: OCEAN.deep,
            }}
          >
            A book to live with.
            <br />A practice to come home to.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-7 md:gap-10">
          {bullets.map((b, i) => (
            <div key={i} data-testid={`home-whats-inside-${i + 1}`}>
              <div
                className="font-serif text-5xl md:text-6xl mb-3 leading-none"
                style={{ color: OCEAN.light }}
              >
                0{i + 1}
              </div>
              <h3
                className="font-serif text-xl md:text-2xl mb-3 leading-snug"
                style={{ color: OCEAN.deep }}
              >
                {b.title}
              </h3>
              <p
                className="font-sans text-[15px] leading-relaxed"
                style={{ color: OCEAN.dark, opacity: 0.82 }}
              >
                {b.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
//  SponsorBanner — Sponsor 100 books to a women's shelter
//  Lives on the homepage between "Inside the Book" and the Testimonial.
//  Tap → goes to /book#sponsor (full 3-tier flow with mailto handler).
//  Mission moment, not a competing product. Aligns with "I walk the talk."
// ----------------------------------------------------------------------------
const SponsorBanner = () => {
  return (
    <section
      data-testid="home-sponsor-banner"
      className="py-14 md:py-20"
      style={{
        background: `linear-gradient(135deg, ${OCEAN.mid} 0%, ${OCEAN.light} 100%)`,
      }}
    >
      <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
        <Heart
          className="w-9 h-9 mx-auto mb-4"
          style={{ color: OCEAN.cream, fill: OCEAN.cream }}
        />
        <p
          className="font-sans text-[11px] tracking-[0.32em] uppercase font-semibold mb-3"
          style={{ color: OCEAN.cream, opacity: 0.9 }}
        >
          Pay it forward
        </p>
        <h2
          className="font-serif tracking-tight leading-[1.1] mb-4"
          style={{
            fontSize: "clamp(1.7rem, 4vw, 2.5rem)",
            color: OCEAN.cream,
          }}
        >
          Sponsor 100 copies to a women&rsquo;s shelter.
        </h2>
        <p
          className="font-serif italic mb-7 max-w-xl mx-auto leading-relaxed"
          style={{
            fontSize: "clamp(0.95rem, 1.6vw, 1.1rem)",
            color: OCEAN.cream,
            opacity: 0.92,
          }}
        >
          Every woman who walks into a domestic violence shelter is, by
          definition, in the messy middle. If this book is the inheritance I
          had to build from scratch — let&rsquo;s put it in the hands of women
          who need it most.
        </p>
        <Link
          to="/book#sponsor"
          data-testid="home-sponsor-cta"
          className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
          style={{ backgroundColor: OCEAN.cream, color: OCEAN.deep }}
        >
          <Heart className="w-4 h-4" style={{ fill: OCEAN.deep }} />
          See the 3 sponsor tiers
          <ArrowRight className="w-4 h-4" />
        </Link>
        <p
          className="font-sans text-xs mt-4 tracking-wide"
          style={{ color: OCEAN.cream, opacity: 0.75 }}
        >
          Or a Circle of 10 &middot; or just one. Every copy lands.
        </p>
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
//  Testimonial — ONE big quote (placeholder — Temple to swap with real reader)
//  See /app/memory/HOMEPAGE_TESTIMONIAL_TODO.md
// ----------------------------------------------------------------------------
const Testimonial = () => {
  return (
    <section
      data-testid="home-testimonial"
      className="py-20 md:py-28"
      style={{ backgroundColor: OCEAN.deep, color: OCEAN.cream }}
    >
      <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
        <Quote
          className="w-10 h-10 mx-auto mb-6 opacity-70"
          style={{ color: OCEAN.light }}
        />
        <blockquote
          data-testid="home-testimonial-quote"
          className="font-serif italic leading-[1.3] mb-8"
          style={{ fontSize: "clamp(1.25rem, 2.6vw, 2rem)" }}
        >
          &ldquo;I just finished your book.{" "}
          <strong style={{ color: OCEAN.light, fontWeight: 600 }}>
            BLEW ME AWAY!
          </strong>{" "}
          I am so in awe of you. I loved every part of it — it took me some
          time, but I loved that, because I was able to take in each chapter. I
          loved the acronyms and breakdown of words, and building new neural
          pathways to what those words mean for you. I hope the launch of your
          book was a MASSIVE success, because not only is this a book, but a
          tool that needs to be studied and applied in many people&rsquo;s
          lives.&rdquo;
        </blockquote>
        <div className="flex flex-col items-center gap-1">
          <p
            data-testid="home-testimonial-name"
            className="font-sans font-semibold tracking-wide text-sm"
            style={{ color: OCEAN.light }}
          >
            — CB
          </p>
          <p
            className="font-sans text-xs tracking-wider uppercase"
            style={{ color: "rgba(244,241,236,0.55)" }}
          >
            First Reader · Washington State
          </p>
        </div>
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
//  Temple's Story — "I&rsquo;ve been her." / "I walk the talk."
// ----------------------------------------------------------------------------
const TempleStory = () => {
  return (
    <section
      data-testid="home-temple-story"
      className="py-20 md:py-28"
      style={{ backgroundColor: OCEAN.cream }}
    >
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <div className="text-center mb-10 md:mb-12">
          <p
            className="font-sans text-[11px] tracking-[0.32em] uppercase font-semibold mb-3"
            style={{ color: OCEAN.mid }}
          >
            Your guide
          </p>
          <h2
            className="font-serif tracking-tighter leading-[1.05] mb-6"
            style={{
              fontSize: "clamp(2rem, 5vw, 3.25rem)",
              color: OCEAN.deep,
            }}
          >
            I built this because no elder was in the room.
          </h2>
          <p
            className="font-serif italic text-lg md:text-xl"
            style={{ color: OCEAN.mid }}
          >
            So I became her.
          </p>
        </div>

        <div
          className="space-y-5 font-sans text-[17px] leading-[1.75]"
          style={{ color: OCEAN.dark }}
        >
          <p className="font-serif italic text-xl" style={{ color: OCEAN.deep }}>
            If you woke up this morning and didn&rsquo;t recognize the woman in
            the mirror — I&rsquo;ve been her.
          </p>

          <p>
            Adopted at birth. Married 22 years to a life eroding under my feet.
            Mother to three daughters and the slow, brutal grief of estrangement.
            Hashimoto&rsquo;s. A breast cancer scare. A bloodwork report that
            kept coming back <em>&ldquo;normal&rdquo;</em> while my body
            screamed otherwise.
          </p>

          <p>
            I was brought down to the <strong>studs</strong>. No frame. No
            insulation. Just the bare beams of who I actually was when
            everything I thought defined me was gone.
          </p>

          <p>
            I read every book. Sat with every teacher. Trained three years with
            a renowned meditation master. Studied Mind, Body, Spirit for over
            twenty years — <em>because no one had given me the language.</em>
          </p>

          <p>
            And then one ordinary morning, at the kitchen trash can, three
            words landed in the middle of my chest…
          </p>

          <p
            className="font-serif italic text-2xl md:text-3xl py-3 pl-5 border-l-4"
            style={{ color: OCEAN.deep, borderColor: OCEAN.light }}
          >
            I GET TO.
          </p>

          <p>
            I get to take out this trash — because I have a home. Because I am
            alive and able. Because the woman I used to be would have given
            anything to be standing where I&rsquo;m standing right now.
          </p>

          <p>
            That moment cracked open everything that became this work. The Mind
            Body Spirit HYGIENE™ Method. The A.C.T. NOW pattern. Every tool
            inside this book.
          </p>

          <p
            className="font-medium"
            style={{ color: OCEAN.deep }}
          >
            This is the inheritance I had to build from scratch — and the one
            I&rsquo;m still using. <strong>I walk the talk.</strong> Every
            morning, every hard conversation, every time the floor falls out
            again — these tools are what I reach for. Not because I figured
            anything out. Because I&rsquo;m two steps ahead, holding the door
            open behind me.
          </p>

          <p className="font-serif italic text-lg" style={{ color: OCEAN.mid }}>
            Whatever brought you to this page — it brought you home.
          </p>

          <p
            className="font-serif text-xl pt-4"
            style={{ color: OCEAN.deep }}
          >
            — Temple
          </p>
        </div>

        <div className="text-center mt-10 md:mt-14">
          <Link
            to="/about"
            data-testid="home-story-read-full-story"
            className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
            style={{ backgroundColor: OCEAN.deep, color: OCEAN.cream }}
          >
            Read my full story
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
//  Final CTA — one repeat of the book buy button
// ----------------------------------------------------------------------------
const FinalCTA = () => {
  const live = isBookLive();
  const onBuyClick = () => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "book_buy_click", {
        source_page: "homepage_final_cta",
        item_name: "I GET TO PDF",
        value: 9.99,
        currency: "USD",
      });
    }
  };

  return (
    <section
      data-testid="home-final-cta"
      className="py-20 md:py-28 text-center"
      style={{
        background: `linear-gradient(180deg, ${OCEAN.cream} 0%, ${OCEAN.mint} 100%)`,
      }}
    >
      <div className="max-w-2xl mx-auto px-5 md:px-8">
        <p
          className="font-sans text-[11px] tracking-[0.32em] uppercase font-semibold mb-4"
          style={{ color: OCEAN.mid }}
        >
          Ready when you are
        </p>
        <h2
          className="font-serif tracking-tighter leading-[1.05] mb-4"
          style={{
            fontSize: "clamp(1.85rem, 4.5vw, 3rem)",
            color: OCEAN.deep,
          }}
        >
          Come home to yourself.
        </h2>
        <p
          className="font-serif italic text-lg md:text-xl mb-8"
          style={{ color: OCEAN.mid }}
        >
          It&rsquo;s not a practice. It&rsquo;s hygiene.
        </p>

        {live ? (
          <>
          <Link
            to={BOOK_CHOOSER}
            onClick={onBuyClick}
            data-testid="home-final-buy-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
            style={{ backgroundColor: OCEAN.deep, color: OCEAN.cream }}
          >
            <ShoppingBag className="w-5 h-5" />
            Get the full book — instant PDF · $9.99
            <ArrowRight className="w-4 h-4" />
          </Link>
          <PaperbackLine testid="home-final-paperback-link" />
          </>
        ) : (
          <Link
            to="/workbook?from=home-final"
            data-testid="home-final-notify-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
            style={{ backgroundColor: OCEAN.deep, color: OCEAN.cream }}
          >
            <Sparkles className="w-5 h-5" />
            Notify me when it ships
          </Link>
        )}

        <p
          className="font-sans text-xs mt-5 tracking-wide"
          style={{ color: OCEAN.dark, opacity: 0.65 }}
        >
          Ships via Lulu &middot; also available on Amazon &amp; Barnes &amp;
          Noble
        </p>
      </div>
    </section>
  );
};

//  Course — Build Your Own, $33 (Temple's exact wording; links to /build-your-own)
const CourseSection = () => (
  <section
    data-testid="home-course-section"
    className="py-20 md:py-28 text-center"
    style={{ background: `linear-gradient(180deg, ${OCEAN.mint} 0%, ${OCEAN.cream} 100%)` }}
  >
    <div className="max-w-2xl mx-auto px-5 md:px-8">
      <p className="font-sans text-[11px] tracking-[0.32em] uppercase font-semibold mb-4" style={{ color: OCEAN.mid }}>
        The course
      </p>
      <h2
        className="font-serif tracking-tighter leading-[1.05] mb-6"
        style={{ fontSize: "clamp(1.85rem, 4.5vw, 3rem)", color: OCEAN.deep }}
        data-testid="home-course-heading"
      >
        Build Your Own
      </h2>
      <div className="space-y-4 font-serif text-lg md:text-xl leading-relaxed mb-8" style={{ color: OCEAN.dark }}>
        <p>Twenty-three short lessons, a pen, and a practice that ends up being yours.</p>
        <p>You read for a few minutes. You do one small thing. You write one line by hand. That is a win, and that is the whole method.</p>
        <p>Some of these tools will become yours. Some will not. Both are a finished lesson.</p>
      </div>
      <Link
        to="/build-your-own"
        data-testid="home-course-cta"
        className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
        style={{ backgroundColor: OCEAN.deep, color: OCEAN.cream }}
      >
        Begin — $33
        <ArrowRight className="w-4 h-4" />
      </Link>
      <p className="font-sans text-xs mt-5 tracking-wide" style={{ color: OCEAN.dark, opacity: 0.65 }}>
        The printable pages and the online practice, together.
      </p>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page" style={{ backgroundColor: OCEAN.cream }}>
      <HomeHeader />
      <main>
        <BookHero />
        <TrustStrip />
        <WhatsInside />
        <SponsorBanner />
        <Testimonial />
        <TempleStory />
        <FinalCTA />
        <CourseSection />
      </main>

    </div>
  );
}
